import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Button,
  Tag,
  Space,
  Spin,
  Typography,
  message,
  Result,
  Empty,
  Row,
  Col,
  Progress,
  Popconfirm,
  Statistic,
} from 'antd';
import {
  TeamOutlined,
  SyncOutlined,
  DeleteOutlined,
  UserOutlined,
  LinkOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { proposalApi } from '../../services/proposal';
import { POLL_CONFIG, showPollTimeoutNotification } from '../../utils/polling';
import type { ReferenceInfluencerList, InfluencerItem } from '../../types/proposal';
import { COLORS, GRADIENTS } from '../../constants/colors';

const { Text, Paragraph, Link } = Typography;

// 数字格式化：粉丝数
const formatFollowerCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
};

// 来源标签
const getSourceTag = (item: InfluencerItem) => {
  if (item.is_cooperated) {
    return <Tag color="green">已合作</Tag>;
  }
  if (item.data_source === 'knowledge_base') {
    return <Tag color="blue">已标注</Tag>;
  }
  if (item.data_source === 'external') {
    return <Tag color="orange">外部发现</Tag>;
  }
  return <Tag>{item.data_source}</Tag>;
};

// 匹配度颜色
const getMatchScoreStatus = (score: number): 'success' | 'normal' | 'exception' => {
  if (score >= 80) return 'success';
  if (score >= 50) return 'normal';
  return 'exception';
};

// 红人卡片组件
const InfluencerCard: React.FC<{
  item: InfluencerItem;
  onDelete: (id: string) => void;
}> = ({ item, onDelete }) => {
  return (
    <Card
      size="small"
      hoverable
      style={{ height: '100%' }}
      styles={{
        body: { padding: 16 },
      }}
      actions={[
        <Popconfirm
          key="delete"
          title="确认移除"
          description="确定要移除此红人吗？"
          onConfirm={() => onDelete(item.influencer_id || '')}
          okText="确定"
          cancelText="取消"
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small">
            移除
          </Button>
        </Popconfirm>,
      ]}
    >
      {/* 头部：头像 + 名称 + 来源 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: GRADIENTS.reference,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
            flexShrink: 0,
          }}
        >
          <UserOutlined style={{ fontSize: 24, color: '#fff' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text strong ellipsis style={{ maxWidth: 120 }}>
              {item.influencer_name}
            </Text>
            {getSourceTag(item)}
          </div>
          <Space size={4}>
            <Tag>{item.platform}</Tag>
            {item.country && <Tag color="cyan">{item.country}</Tag>}
          </Space>
        </div>
      </div>

      {/* 频道链接 */}
      {item.channel_url && (
        <div style={{ marginBottom: 12 }}>
          <Link href={item.channel_url} target="_blank" ellipsis>
            <LinkOutlined style={{ marginRight: 4 }} />
            {item.channel_url}
          </Link>
        </div>
      )}

      {/* 数据统计 */}
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col span={12}>
          <Statistic
            title="粉丝量"
            value={formatFollowerCount(item.follower_count)}
            valueStyle={{ fontSize: 16 }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title="近30天均播放"
            value={formatFollowerCount(item.avg_views_30d)}
            valueStyle={{ fontSize: 16 }}
          />
        </Col>
      </Row>

      {/* 匹配度 */}
      <div style={{ marginBottom: 12, textAlign: 'center' }}>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">匹配度</Text>
        </div>
        <Progress
          type="circle"
          percent={item.match_score}
          width={60}
          status={getMatchScoreStatus(item.match_score)}
          format={(percent) => `${percent}%`}
        />
      </div>

      {/* 推荐理由 */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <StarOutlined style={{ marginRight: 4 }} />
            AI 推荐理由
          </Text>
        </div>
        <Paragraph
          ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
          style={{ marginBottom: 0, fontSize: 12 }}
        >
          {item.match_reason || '暂无'}
        </Paragraph>
      </div>

      {/* 内容类别 */}
      {item.content_categories && item.content_categories.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>内容类别</Text>
          </div>
          <Space wrap size={4}>
            {item.content_categories.slice(0, 3).map((cat, index) => (
              <Tag key={index} color="purple" style={{ fontSize: 11 }}>
                {cat}
              </Tag>
            ))}
            {item.content_categories.length > 3 && (
              <Tag style={{ fontSize: 11 }}>+{item.content_categories.length - 3}</Tag>
            )}
          </Space>
        </div>
      )}

      {/* 已合作红人额外信息 */}
      {item.is_cooperated && item.cooperation_history && (
        <Card
          size="small"
          style={{ background: COLORS.bgSuccessLight, border: `1px solid ${COLORS.borderSuccess}` }}
          styles={{ body: { padding: 8 } }}
        >
          <div style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>历史合作数据</Text>
          </div>
          <Row gutter={8}>
            {item.cooperation_history.cpm !== undefined && (
              <Col span={8}>
                <Text style={{ fontSize: 11 }}>CPM: </Text>
                <Text strong style={{ fontSize: 11, color: COLORS.success }}>
                  ${item.cooperation_history.cpm}
                </Text>
              </Col>
            )}
            {item.cooperation_history.conversion_rate !== undefined && (
              <Col span={8}>
                <Text style={{ fontSize: 11 }}>转化率: </Text>
                <Text strong style={{ fontSize: 11, color: COLORS.success }}>
                  {(item.cooperation_history.conversion_rate * 100).toFixed(1)}%
                </Text>
              </Col>
            )}
            {item.cooperation_history.cooperation_score !== undefined && (
              <Col span={8}>
                <Text style={{ fontSize: 11 }}>配合度: </Text>
                <Text strong style={{ fontSize: 11, color: COLORS.success }}>
                  {item.cooperation_history.cooperation_score}/5
                </Text>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {/* 优势与风险 */}
      {(item.strengths?.length || item.risks?.length) && (
        <div style={{ marginTop: 8 }}>
          {item.strengths && item.strengths.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>优势: </Text>
              <Space size={2} wrap>
                {item.strengths.slice(0, 2).map((s, i) => (
                  <Tag key={i} color="green" style={{ fontSize: 10 }}>{s}</Tag>
                ))}
              </Space>
            </div>
          )}
          {item.risks && item.risks.length > 0 && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>风险: </Text>
              <Space size={2} wrap>
                {item.risks.slice(0, 2).map((r, i) => (
                  <Tag key={i} color="red" style={{ fontSize: 10 }}>{r}</Tag>
                ))}
              </Space>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

interface ReferenceTabProps {
  proposalId: string;
  references?: ReferenceInfluencerList | null;
  onRefresh: () => void;
}

export const ReferenceTab: React.FC<ReferenceTabProps> = ({
  proposalId,
  references,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // 生成推荐
  const handleGenerate = async () => {
    setLoading(true);
    try {
      await proposalApi.generateReferences(proposalId);
      message.success('AI参考红人推荐已启动');
      startPolling();
    } catch (error) {
      message.error('启动参考红人生成失败，请重试');
      setLoading(false);
    }
  };

  // 轮询检查状态
  const startPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    let pollAttempts = 0;
    timerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-reference', () => {
          startPolling();
        });
        setLoading(false);
        return;
      }
      try {
        const response = await proposalApi.getReferences(proposalId);
        const result = response.data;
        if (result && (result.status === 'completed' || result.status === 'failed')) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          setLoading(false);
          onRefresh();
          if (result.status === 'failed') {
            message.error('参考红人生成失败');
          } else {
            message.success('参考红人生成完成');
          }
        }
      } catch (error) {
        // 继续轮询
      }
    }, POLL_CONFIG.INTERVAL);
  };

  // 删除红人
  const handleDelete = async (referenceId: string) => {
    try {
      await proposalApi.deleteReference(referenceId);
      message.success('已移除');
      onRefresh();
    } catch (error) {
      message.error('移除失败，请重试');
    }
  };

  // 未生成状态
  if (!references || references.status === 'pending' || references.status === 'draft') {
    if (loading) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 24 }}>
              <Text type="secondary" style={{ fontSize: 16 }}>
                AI 正在为您推荐匹配的红人，请稍候...
              </Text>
            </div>
          </div>
        </Card>
      );
    }

    return (
      <Card>
        <Empty
          description={
            <span>
              暂无参考红人推荐
              <br />
              <Text type="secondary">点击下方按钮生成 AI 红人推荐</Text>
            </span>
          }
        >
          <Button
            type="primary"
            icon={<TeamOutlined />}
            onClick={handleGenerate}
            loading={loading}
            size="large"
          >
            生成推荐
          </Button>
        </Empty>
      </Card>
    );
  }

  // 处理中状态
  if (references.status === 'processing' || loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 24 }}>
            <Text type="secondary" style={{ fontSize: 16 }}>
              AI 正在为您推荐匹配的红人，请稍候...
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  // 失败状态
  if (references.status === 'failed') {
    return (
      <Card>
        <Result
          status="error"
          title="参考红人生成失败"
          subTitle="生成过程中发生错误，请重试"
          extra={
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleGenerate}
            >
              重新生成
            </Button>
          }
        />
      </Card>
    );
  }

  // 已生成状态 - 按 match_score 降序排列
  const sortedInfluencers = [...(references.influencers || [])].sort(
    (a, b) => b.match_score - a.match_score
  );

  return (
    <Card>
      {/* 顶部操作栏 */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Text strong>
            共推荐 {sortedInfluencers.length} 位红人
          </Text>
          <Text type="secondary">
            (按匹配度排序)
          </Text>
        </Space>
        <Button icon={<SyncOutlined />} onClick={handleGenerate}>
          重新生成
        </Button>
      </div>

      {/* 红人卡片网格 */}
      {sortedInfluencers.length > 0 ? (
        <Row gutter={[16, 16]}>
          {sortedInfluencers.map((item, index) => (
            <Col key={item.influencer_id || index} xs={24} sm={12} lg={8}>
              <InfluencerCard item={item} onDelete={handleDelete} />
            </Col>
          ))}
        </Row>
      ) : (
        <Empty description="暂无推荐红人" />
      )}
    </Card>
  );
};
